<script lang="ts" setup>
import { computed, watch, ref, type FunctionDirective } from 'vue';
import type { Components } from '@workbench/core';
import { useTrackingStore } from '@dh-io-owpi/shared/src/stores/tracking';
import { useAemData } from '@dh-io-owpi/shared/src/plugins/aemData';
import { useLoading } from '@dh-io-owpi/shared/src/composables/useLoading';
import { formatPrice } from '@dh-io-owpi/shared-dynamic-stage/src/utils/formatPrice';
import type { StageVehicle } from '../../lib/api';

const { createTrackOnceFn } = useTrackingStore();
const trackTooltipHover = createTrackOnceFn({ action: 'salesbar', label: 'price_tooltip', category: 'feature' });

const aemData = useAemData();
const { isLoading } = useLoading('stage');

type TPosition = Components.WbTooltipHost['position'];

const props = defineProps<{
  stageVehicle?: StageVehicle;
  isDark: boolean;
  purchasePriceTooltip?: string;
  position?: TPosition;
}>();

const stageVehicle = ref(props.stageVehicle);
// When changing stageVehicle, only apply it's values after the image is loaded.
// Ensure that we never show a vehicle price that does not correspond to the visible image.
watch([() => props.stageVehicle, isLoading], ([newVehicle, loading], [oldVehicle]) => {
  if (!oldVehicle || !loading) stageVehicle.value = newVehicle;
});

const formattedPrice = computed(
  () => stageVehicle.value?.vehiclePrice && formatPrice(aemData, stageVehicle.value?.vehiclePrice),
);

// set position attribute on wb-tooltip-host after mount and when position changes, avoid having a tooltip with the wrong position
const vTipPosition: FunctionDirective<HTMLDivElement & Components.WbTooltipHost, TPosition | undefined> = (el, b) =>
  el.setAttribute('position', b.value!);
</script>
<template>
  <div class="owpi-price-tag wb-type-copy-strong">
    <template v-if="formattedPrice">
      <span>{{ formattedPrice }}</span>
      <wb-tooltip-host v-if="purchasePriceTooltip" v-tip-position="position" @mouseover="trackTooltipHover">
        <wb-icon name="bds/info-circle/24" />
        <wb-tooltip>{{ purchasePriceTooltip }}</wb-tooltip>
      </wb-tooltip-host>
    </template>
    <wb-skeleton v-else :theme="props.isDark ? 'dark' : 'light'">&nbsp;</wb-skeleton>
  </div>
</template>
<style lang="scss" scoped>
.owpi-price-tag {
  color: var(--psb-c-value);
  wb-skeleton {
    min-width: 12ch;
  }
  wb-icon:not(.hydrated),
  wb-tooltip-host:not(.hydrated) {
    display: none;
  }

  display: flex;
  align-items: center;
  gap: var(--wb-spacing-xxs);
  :deep(wb-icon) {
    cursor: pointer;
    width: 20px;
    height: 20px;

    &[name*='/'] {
      width: 16px;
      height: 16px;
    }
  }
}
</style>
